'use client';

import type React from 'react';
import { useState } from 'react';

import { Collaborator } from '@/app/models/collaborator';
import { useSearchPersonQuery } from '@/app/services/personApi';
import DataTable, { bodyCellSx } from '@/components/common/DataTable';
import { Checkbox, TableCell, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConsiderCollaboratorTableProps {
  searchTerm: string;
  selectedCollaborators: number[];
  setSelectedCollaborators: (id: number[]) => void;
}

export default function ConsiderCollaboratorTable({
  searchTerm,
  selectedCollaborators,
  setSelectedCollaborators,
}: ConsiderCollaboratorTableProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data: collaborators } = useSearchPersonQuery({
    searchTerm,
    available: false,
    page,
    size: rowsPerPage,
    sortKey: 'name',
    sortDirection: 'ASC',
  });
  const { t } = useTranslation('considerCollaborator');

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectedCollaborators, id]
      : selectedCollaborators.filter((collabId) => collabId !== id);

    setSelectedCollaborators(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collaborators?.content.map((collab) => collab.id);
      setSelectedCollaborators(allIds as number[]);
    } else {
      setSelectedCollaborators([]);
    }
  };

  const columns = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={
            selectedCollaborators.length > 0 &&
            selectedCollaborators.length < (collaborators?.content?.length as number)
          }
          checked={
            (collaborators?.content?.length as number) > 0 &&
            selectedCollaborators.length === collaborators?.content.length
          }
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6), // Small width for checkbox column
    },
    { key: 'collaborator', label: t('collaborator') },
    { key: 'position', label: t('position') },
  ];

  const renderRow = (collaborator: Collaborator) => {
    const isSelected = selectedCollaborators.includes(collaborator.id);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(collaborator.id, e.target.checked)}
            inputProps={{ 'aria-labelledby': `collaborator-${collaborator.id}` }}
          />
        </TableCell>
        <TableCell sx={bodyCellSx} id={`collaborator-${collaborator.id}`}>
          <Typography variant="body2">
            {collaborator.badge} - {collaborator.name}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Typography variant="body2">{collaborator.position}</Typography>
        </TableCell>
      </>
    );
  };

  // Use the collaborator ID as the unique key
  const getRowKey = (collaborator: Collaborator) => collaborator.id;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: collaborators?.totalElements || 0,
    rowsPerPageOptions: [5, 10, 25],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('de')} ${count}`,
    labelRowsPerPage: t('itens.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={collaborators?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      pagination={paginationConfig}
      size="small"
    />
  );
}
